import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatDateTR, todayISO } from "@/lib/format";
import { PageHeader, AddButton, Field, inputCls } from "@/components/shared";
import { usePermission } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import {
  Trash2, X, ArrowDownRight, ArrowUpRight, CreditCard, Banknote,
  Calendar, Lock, Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight, ClipboardList, BarChart3,
  FileDown, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = [
  { code: "TRY", symbol: "₺", label: "Türk Lirası" },
  { code: "USD", symbol: "$", label: "Dolar" },
  { code: "EUR", symbol: "€", label: "Euro" },
];

const METHODS = [
  { key: "cash", label: "Nakit", icon: Banknote },
  { key: "credit_card", label: "Kredi Kartı", icon: CreditCard },
];

const fmt = (v, cur) => {
  try {
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(Number(v || 0));
  } catch { return `${v} ${cur}`; }
};

const emptyForm = { kind: "in", currency: "TRY", amount: "", description: "" };
const BACKDATED_MAX_DAYS = 2;

// Compute date N days ago as ISO string (local)
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function Cash({ branch = "main", title = "Merkez Kasa", section = "cash", testidPrefix = "cash" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = usePermission(section, "edit");
  const [tab, setTab] = useState("daily"); // daily | monthly | approvals
  const [method, setMethod] = useState("cash");

  return (
    <div className="p-6 md:p-10 lg:p-12 fade-in max-w-[1400px]">
      <PageHeader eyebrow="Nakit kasa" title={title} description="Günlük kasa — devir bakiye ile takip edilir." />

      <div className="mb-6 inline-flex bg-white border border-[#E6E4DD] rounded-2xl p-1" data-testid={`${testidPrefix}-view-tabs`}>
        <TabBtn active={tab === "daily"} onClick={() => setTab("daily")} icon={Calendar} label="Günlük Kasa" testid={`${testidPrefix}-view-daily`} />
        <TabBtn active={tab === "monthly"} onClick={() => setTab("monthly")} icon={BarChart3} label="Aylık Rapor" testid={`${testidPrefix}-view-monthly`} />
        {isAdmin && (
          <TabBtn active={tab === "approvals"} onClick={() => setTab("approvals")} icon={ClipboardList} label="Bekleyen Onaylar" testid={`${testidPrefix}-view-approvals`} />
        )}
      </div>

      <div className="mb-6 inline-flex ml-3 bg-white border border-[#E6E4DD] rounded-2xl p-1" data-testid={`${testidPrefix}-tabs`}>
        {METHODS.map((m) => {
          const active = method === m.key;
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              onClick={() => setMethod(m.key)}
              data-testid={`${testidPrefix}-tab-${m.key}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                active ? "bg-[#8A5A44] text-white" : "text-[#5C5B57] hover:bg-[#F0EFEA]"
              }`}
            >
              <Icon size={16} strokeWidth={1.75} /> {m.label}
            </button>
          );
        })}
      </div>

      {tab === "daily" && (
        <DailyView branch={branch} method={method} section={section} testidPrefix={testidPrefix} isAdmin={isAdmin} canEdit={canEdit} />
      )}
      {tab === "monthly" && (
        <MonthlyReport branch={branch} method={method} testidPrefix={testidPrefix} />
      )}
      {tab === "approvals" && isAdmin && (
        <AdminApprovals branch={branch} method={method} testidPrefix={testidPrefix} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
        active ? "bg-[#2D2C2A] text-white" : "text-[#5C5B57] hover:bg-[#F0EFEA]"
      }`}
    >
      <Icon size={16} strokeWidth={1.75} /> {label}
    </button>
  );
}

// ============================================
// DAILY VIEW
// ============================================
function DailyView({ branch, method, section, testidPrefix, isAdmin, canEdit }) {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [daily, setDaily] = useState({
    balances: { TRY: { opening: 0, in: 0, out: 0, closing: 0 }, USD: { opening: 0, in: 0, out: 0, closing: 0 }, EUR: { opening: 0, in: 0, out: 0, closing: 0 } },
    transactions: [], today: todayISO(), is_admin: false,
  });
  const [pendingReqs, setPendingReqs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  const today = daily.today || todayISO();
  const isToday = selectedDate === today;
  const daysBack = useMemo(() => {
    const a = new Date(today), b = new Date(selectedDate);
    return Math.round((a - b) / (1000 * 60 * 60 * 24));
  }, [today, selectedDate]);
  const isBackdated = daysBack > 0 && daysBack <= BACKDATED_MAX_DAYS;
  const isFuture = daysBack < 0;
  const isTooFar = daysBack > BACKDATED_MAX_DAYS;

  // Admin: can add any day. User: today OR backdated (up to 2 days) as request
  const canAddDirect = canEdit && (isAdmin || isToday);
  const canRequest = canEdit && !isAdmin && isBackdated;
  const canOpenAdd = canAddDirect || canRequest;

  const load = useCallback(async () => {
    const { data } = await api.get("/cash/daily", { params: { branch, method, date_str: selectedDate } });
    setDaily(data);
    // Load pending requests for this date (own for users; all for admin)
    try {
      const { data: reqs } = await api.get("/cash/requests", {
        params: { branch, method, status: "pending", scope: isAdmin ? "all" : "mine" },
      });
      setPendingReqs((reqs || []).filter((r) => r.date === selectedDate));
    } catch { setPendingReqs([]); }
  }, [branch, method, selectedDate, isAdmin]);
  useEffect(() => { load(); }, [load]);

  // Poll for user's decided notifications
  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/cash/requests/my-notifications");
        if (cancelled) return;
        (data || []).forEach((n) => {
          if (n.status === "approved") {
            toast.success(`Onay talebiniz onaylandı: ${fmt(n.amount, n.currency)} (${formatDateTR(n.date)})`);
          } else if (n.status === "rejected") {
            toast.error(`Talep reddedildi: ${fmt(n.amount, n.currency)} — ${n.reject_reason || "Sebep belirtilmedi"}`);
          }
        });
        if ((data || []).length) load();
      } catch {}
    };
    check();
    const t = setInterval(check, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAdmin, load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, branch, method, date: selectedDate, amount: parseFloat(form.amount) || 0 };
      if (isAdmin || isToday) {
        await api.post("/cash", payload);
        toast.success((method === "cash" ? "Nakit" : "Kredi kartı") + " hareketi eklendi");
      } else {
        await api.post("/cash/requests", payload);
        toast.success("Onay talebiniz gönderildi. Admin onayı bekleniyor.");
      }
      setOpen(false); setForm({ ...emptyForm }); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt başarısız");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try {
      await api.delete(`/cash/${id}`); toast.success("Silindi"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  const cancelReq = async (id) => {
    if (!window.confirm("Bu onay talebini iptal etmek istediğinizden emin misiniz?")) return;
    try {
      await api.delete(`/cash/requests/${id}`); toast.success("Talep iptal edildi"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İptal edilemedi");
    }
  };

  const summary = daily.balances;
  const items = daily.transactions;

  const minDate = daysAgoISO(BACKDATED_MAX_DAYS);
  const maxDate = today;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4 bg-white border border-[#E6E4DD] rounded-2xl p-4" data-testid={`${testidPrefix}-date-bar`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#8A5A44]/10 text-[#8A5A44] flex items-center justify-center"><Calendar size={18} /></div>
          <div>
            <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#5C5B57]">Gün</p>
            <input
              type="date"
              className="text-lg font-heading text-[#2D2C2A] bg-transparent border-none outline-none cursor-pointer"
              value={selectedDate}
              min={isAdmin ? undefined : minDate}
              max={isAdmin ? undefined : maxDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              data-testid={`${testidPrefix}-date-picker`}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => setSelectedDate(today)} className="text-xs px-3 py-1.5 rounded-lg border border-[#E6E4DD] hover:bg-[#F0EFEA]" data-testid={`${testidPrefix}-today-btn`}>Bugün</button>
          {isFuture && (
            <span className="text-xs px-3 py-1.5 rounded-lg bg-[#B24C4C]/15 text-[#B24C4C] font-medium inline-flex items-center gap-1">
              <Lock size={12}/> Gelecek tarih seçilemez
            </span>
          )}
          {!isToday && !isFuture && (
            isAdmin ? (
              <span className="text-xs px-3 py-1.5 rounded-lg bg-[#D4A373]/20 text-[#8A5A44] font-medium inline-flex items-center gap-1" data-testid={`${testidPrefix}-past-badge`}>
                Geçmiş tarih (admin)
              </span>
            ) : isBackdated ? (
              <span className="text-xs px-3 py-1.5 rounded-lg bg-[#D4A373]/20 text-[#8A5A44] font-medium inline-flex items-center gap-1" data-testid={`${testidPrefix}-request-badge`}>
                <Clock size={12}/> Onay talebi ile eklenebilir ({daysBack} gün önce)
              </span>
            ) : (
              <span className="text-xs px-3 py-1.5 rounded-lg bg-[#B24C4C]/15 text-[#B24C4C] font-medium inline-flex items-center gap-1">
                <Lock size={12}/> Bu tarihe kayıt yapılamaz (max {BACKDATED_MAX_DAYS} gün geriye)
              </span>
            )
          )}
          {canOpenAdd && (
            <AddButton
              onClick={() => { setForm({ ...emptyForm }); setOpen(true); }}
              label={canRequest ? "Onay Talebi Gönder" : (method === "cash" ? "Yeni Nakit Hareket" : "Yeni Kredi Kartı Hareket")}
              testid={`add-${testidPrefix}-btn`}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {CURRENCIES.map((c) => {
          const s = summary[c.code] || { opening: 0, in: 0, out: 0, closing: 0 };
          return (
            <div key={c.code} className="bg-white border border-[#E6E4DD] rounded-2xl p-6" data-testid={`cash-card-${c.code}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5B57]">{c.label}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#5C5B57] mt-2">Gün Sonu Bakiye</p>
                  <p className="font-heading text-3xl text-[#2D2C2A]" data-testid={`cash-closing-${c.code}`}>{fmt(s.closing, c.code)}</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-[#8A5A44]/10 text-[#8A5A44] flex items-center justify-center font-heading text-xl">{c.symbol}</div>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-4 border-t border-[#E6E4DD]">
                <div><p className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#5C5B57]">Devir</p><p className="font-mono text-xs text-[#2D2C2A]" data-testid={`cash-opening-${c.code}`}>{fmt(s.opening, c.code)}</p></div>
                <div><p className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#4A6741] flex items-center gap-1"><ArrowDownRight size={10}/> Giriş</p><p className="font-mono text-xs text-[#2D2C2A]">{fmt(s.in, c.code)}</p></div>
                <div><p className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#B24C4C] flex items-center gap-1"><ArrowUpRight size={10}/> Çıkış</p><p className="font-mono text-xs text-[#2D2C2A]">{fmt(s.out, c.code)}</p></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-[#E6E4DD] rounded-2xl overflow-hidden">
        <table className="w-full text-left" data-testid="cash-table">
          <thead><tr className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C5B57] border-b border-[#E6E4DD]">
            <th className="px-6 py-4">Saat/Tarih</th>
            <th className="px-6 py-4">Tür</th>
            <th className="px-6 py-4">Para Birimi</th>
            <th className="px-6 py-4">Tutar</th>
            <th className="px-6 py-4 w-[35%]">Açıklama</th>
            <th className="px-6 py-4">Durum</th>
            <th className="px-6 py-4 text-right">İşlem</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && pendingReqs.length === 0 && (
              <tr><td colSpan="7" className="px-6 py-12 text-center text-[#5C5B57] text-sm">Bu güne ait hareket yok.</td></tr>
            )}
            {items.map((t) => (
              <tr key={t.id} className="border-b border-[#E6E4DD]/60 hover:bg-[#F0EFEA]/40" data-testid={`cash-row-${t.id}`}>
                <td className="px-6 py-4 text-[#5C5B57] whitespace-nowrap">{formatDateTR(t.date)}</td>
                <td className="px-6 py-4"><span className={`inline-block text-xs px-2.5 py-1 rounded-full font-medium ${t.kind === "in" ? "bg-[#4A6741]/15 text-[#4A6741]" : "bg-[#B24C4C]/15 text-[#B24C4C]"}`}>{t.kind === "in" ? "Giriş" : "Çıkış"}</span></td>
                <td className="px-6 py-4 font-mono text-sm">{t.currency}</td>
                <td className={`px-6 py-4 font-mono font-medium whitespace-nowrap ${t.kind === "in" ? "text-[#4A6741]" : "text-[#B24C4C]"}`}>{t.kind === "in" ? "+" : "-"}{fmt(t.amount, t.currency)}</td>
                <td className="px-6 py-4 text-[#5C5B57] text-sm whitespace-normal break-words align-top">{t.description || "-"}</td>
                <td className="px-6 py-4"><span className="inline-block text-xs px-2.5 py-1 rounded-full font-medium bg-[#4A6741]/15 text-[#4A6741]">Onaylı</span></td>
                <td className="px-6 py-4 text-right">{canEdit && (isAdmin || isToday) && (<button onClick={() => remove(t.id)} data-testid={`delete-cash-${t.id}`} className="p-2 rounded-lg hover:bg-[#B24C4C]/10 text-[#B24C4C]"><Trash2 size={16} /></button>)}</td>
              </tr>
            ))}
            {pendingReqs.map((r) => (
              <tr key={`req-${r.id}`} className="border-b border-[#E6E4DD]/60 bg-[#F0EFEA]/60 opacity-75" data-testid={`cash-pending-row-${r.id}`}>
                <td className="px-6 py-4 text-[#5C5B57] whitespace-nowrap">{formatDateTR(r.date)}</td>
                <td className="px-6 py-4"><span className={`inline-block text-xs px-2.5 py-1 rounded-full font-medium ${r.kind === "in" ? "bg-[#4A6741]/10 text-[#4A6741]/70" : "bg-[#B24C4C]/10 text-[#B24C4C]/70"}`}>{r.kind === "in" ? "Giriş" : "Çıkış"}</span></td>
                <td className="px-6 py-4 font-mono text-sm text-[#5C5B57]">{r.currency}</td>
                <td className={`px-6 py-4 font-mono font-medium whitespace-nowrap text-[#5C5B57]`}>{r.kind === "in" ? "+" : "-"}{fmt(r.amount, r.currency)}</td>
                <td className="px-6 py-4 text-[#5C5B57] text-sm whitespace-normal break-words align-top italic">
                  {r.description || "-"}
                  <div className="text-[10px] mt-1 opacity-70">Talep eden: {r.user_name || r.user_email}</div>
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-[#D4A373]/20 text-[#8A5A44]">
                    <Clock size={11}/> Onay Bekliyor
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  {!isAdmin && r.user_id === currentUserId && (
                    <button onClick={() => cancelReq(r.id)} data-testid={`cancel-req-${r.id}`} className="p-2 rounded-lg hover:bg-[#B24C4C]/10 text-[#B24C4C]"><X size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <AddModal
          method={method}
          canRequest={canRequest}
          selectedDate={selectedDate}
          isToday={isToday}
          isAdmin={isAdmin}
          form={form}
          setForm={setForm}
          onClose={() => setOpen(false)}
          onSubmit={submit}
        />
      )}
    </>
  );
}

function AddModal({ method, canRequest, selectedDate, isToday, isAdmin, form, setForm, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="cash-form-modal">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading text-2xl text-[#2D2C2A]">
            {canRequest ? "Onay Talebi Gönder" : (method === "cash" ? "Yeni Nakit Hareket" : "Yeni Kredi Kartı Hareket")}
          </h3>
          <button onClick={onClose} className="text-[#5C5B57]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#5C5B57] mb-6">
          Tarih: <strong className="text-[#2D2C2A]">{formatDateTR(selectedDate)}</strong>
          {canRequest && <span className="block mt-1 text-[#8A5A44]">Bu kayıt admin onayına gönderilecek. Onaylanana kadar bakiyeye yansımayacak.</span>}
          {!isToday && !isAdmin && !canRequest && <span className="text-[#B24C4C]"> — geçmiş tarihe kayıt için admin gereklidir</span>}
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tür">
              <select className={inputCls} data-testid="cash-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                <option value="in">Giriş</option><option value="out">Çıkış</option>
              </select>
            </Field>
            <Field label="Para Birimi">
              <select className={inputCls} data-testid="cash-currency-select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
              </select>
            </Field>
          </div>
          <Field label="Tutar">
            <input required type="number" step="0.01" min="0" className={inputCls} data-testid="cash-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </Field>
          <Field label="Açıklama">
            <textarea rows={2} className={inputCls} data-testid="cash-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#E6E4DD] rounded-xl px-6 py-3 hover:bg-[#F0EFEA]">İptal</button>
            <button type="submit" data-testid="cash-submit" className="flex-1 bg-[#8A5A44] text-white hover:bg-[#714835] rounded-xl px-6 py-3 font-medium">
              {canRequest ? "Talep Gönder" : "Ekle"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// MONTHLY REPORT
// ============================================
function MonthlyReport({ branch, method, testidPrefix }) {
  const [monthsBack, setMonthsBack] = useState(6);
  const [report, setReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [detail, setDetail] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/cash/monthly-report", { params: { branch, method, months: monthsBack } });
      setReport(data);
    })();
  }, [branch, method, monthsBack]);

  useEffect(() => {
    if (!selectedMonth) { setDetail(null); return; }
    (async () => {
      const { data } = await api.get("/cash/monthly-detail", { params: { branch, method, month: selectedMonth } });
      setDetail(data);
    })();
  }, [branch, method, selectedMonth]);

  const downloadReport = async (format) => {
    setDownloading(format);
    try {
      const res = await api.get(`/cash/monthly-report/export/${format}`, {
        params: { branch, method, months: monthsBack },
        responseType: "blob",
      });
      const ext = format === "excel" ? "xlsx" : "pdf";
      const filename = `kasa-${branch}-${method}-aylik-rapor.${ext}`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format === "excel" ? "Excel" : "PDF"} indirildi`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "İndirme başarısız");
    } finally { setDownloading(""); }
  };

  if (!report) return <div className="text-[#5C5B57]">Yükleniyor...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 bg-white border border-[#E6E4DD] rounded-2xl p-4 flex-wrap justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-[#5C5B57]">Karşılaştırma Aralığı</span>
          <div className="flex gap-1">
            {[3, 6, 12].map((n) => (
              <button
                key={n}
                onClick={() => setMonthsBack(n)}
                data-testid={`${testidPrefix}-months-${n}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${monthsBack === n ? "bg-[#2D2C2A] text-white" : "border border-[#E6E4DD] text-[#5C5B57] hover:bg-[#F0EFEA]"}`}
              >{n} ay</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadReport("excel")}
            disabled={!!downloading}
            data-testid={`${testidPrefix}-export-excel`}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium bg-[#4A6741] text-white hover:bg-[#3d5636] disabled:opacity-50"
          >
            <FileSpreadsheet size={14}/> {downloading === "excel" ? "İndiriliyor..." : "Excel"}
          </button>
          <button
            onClick={() => downloadReport("pdf")}
            disabled={!!downloading}
            data-testid={`${testidPrefix}-export-pdf`}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium bg-[#B24C4C] text-white hover:bg-[#943a3a] disabled:opacity-50"
          >
            <FileDown size={14}/> {downloading === "pdf" ? "İndiriliyor..." : "PDF"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E6E4DD] rounded-2xl overflow-x-auto" data-testid={`${testidPrefix}-monthly-table`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C5B57] border-b border-[#E6E4DD]">
              <th className="px-6 py-4 text-left">Ay</th>
              {CURRENCIES.map((c) => (
                <React.Fragment key={c.code}>
                  <th className="px-4 py-4 text-right">{c.code} Giriş</th>
                  <th className="px-4 py-4 text-right">{c.code} Çıkış</th>
                  <th className="px-4 py-4 text-right">{c.code} Net</th>
                </React.Fragment>
              ))}
              <th className="px-4 py-4 text-right">İşlem</th>
              <th className="px-4 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {report.months.map((m) => (
              <tr key={m.month} className="border-b border-[#E6E4DD]/60 hover:bg-[#F0EFEA]/40" data-testid={`${testidPrefix}-monthly-row-${m.month}`}>
                <td className="px-6 py-4 font-medium text-[#2D2C2A]">{m.label}</td>
                {CURRENCIES.map((c) => {
                  const t = m.totals[c.code] || { in: 0, out: 0, net: 0 };
                  return (
                    <React.Fragment key={c.code}>
                      <td className="px-4 py-4 text-right font-mono text-[#4A6741]">{fmt(t.in, c.code)}</td>
                      <td className="px-4 py-4 text-right font-mono text-[#B24C4C]">{fmt(t.out, c.code)}</td>
                      <td className={`px-4 py-4 text-right font-mono font-medium ${t.net >= 0 ? "text-[#4A6741]" : "text-[#B24C4C]"}`}>{fmt(t.net, c.code)}</td>
                    </React.Fragment>
                  );
                })}
                <td className="px-4 py-4 text-right text-[#5C5B57]">{m.count}</td>
                <td className="px-4 py-4 text-right">
                  <button
                    onClick={() => setSelectedMonth(selectedMonth === m.month ? "" : m.month)}
                    data-testid={`${testidPrefix}-monthly-detail-${m.month}`}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#E6E4DD] hover:bg-[#F0EFEA]"
                  >{selectedMonth === m.month ? "Kapat" : "Detay"}</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-[#F0EFEA] border-t-2 border-[#8A5A44]" data-testid={`${testidPrefix}-monthly-grand-total`}>
              <td className="px-6 py-4 font-heading text-[#2D2C2A]">GENEL TOPLAM</td>
              {CURRENCIES.map((c) => {
                const t = report.grand_total[c.code] || { in: 0, out: 0, net: 0 };
                return (
                  <React.Fragment key={c.code}>
                    <td className="px-4 py-4 text-right font-mono font-bold text-[#4A6741]">{fmt(t.in, c.code)}</td>
                    <td className="px-4 py-4 text-right font-mono font-bold text-[#B24C4C]">{fmt(t.out, c.code)}</td>
                    <td className={`px-4 py-4 text-right font-mono font-bold ${t.net >= 0 ? "text-[#4A6741]" : "text-[#B24C4C]"}`}>{fmt(t.net, c.code)}</td>
                  </React.Fragment>
                );
              })}
              <td className="px-4 py-4 text-right font-bold text-[#2D2C2A]">{report.grand_count}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {detail && (
        <div className="bg-white border border-[#E6E4DD] rounded-2xl overflow-hidden" data-testid={`${testidPrefix}-monthly-detail-panel`}>
          <div className="p-4 border-b border-[#E6E4DD] flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5C5B57]">Ay Detayı</p>
              <h4 className="font-heading text-xl text-[#2D2C2A]">{detail.label}</h4>
            </div>
            <div className="flex flex-wrap gap-3">
              {CURRENCIES.map((c) => (
                <div key={c.code} className="text-right">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#5C5B57]">{c.code} Devir → Kapanış</p>
                  <p className="font-mono text-sm">
                    <span className="text-[#5C5B57]">{fmt(detail.opening_balance[c.code], c.code)}</span>
                    <span className="text-[#5C5B57]"> → </span>
                    <span className={`font-bold ${detail.closing_balance[c.code] >= 0 ? "text-[#4A6741]" : "text-[#B24C4C]"}`}>{fmt(detail.closing_balance[c.code], c.code)}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C5B57] border-b border-[#E6E4DD]">
                <th className="px-4 py-3 text-left">Tarih</th>
                <th className="px-4 py-3">Tür</th>
                <th className="px-4 py-3">Para Birimi</th>
                <th className="px-4 py-3 text-right">Tutar</th>
                <th className="px-4 py-3 text-left w-[45%]">Açıklama</th>
              </tr>
            </thead>
            <tbody>
              {detail.transactions.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-[#5C5B57]">Bu ayda kayıt yok.</td></tr>
              )}
              {detail.transactions.map((t) => (
                <tr key={t.id} className="border-b border-[#E6E4DD]/60">
                  <td className="px-4 py-3 text-[#5C5B57] whitespace-nowrap">{formatDateTR(t.date)}</td>
                  <td className="px-4 py-3"><span className={`inline-block text-xs px-2 py-0.5 rounded-full ${t.kind === "in" ? "bg-[#4A6741]/15 text-[#4A6741]" : "bg-[#B24C4C]/15 text-[#B24C4C]"}`}>{t.kind === "in" ? "Giriş" : "Çıkış"}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{t.currency}</td>
                  <td className={`px-4 py-3 text-right font-mono whitespace-nowrap ${t.kind === "in" ? "text-[#4A6741]" : "text-[#B24C4C]"}`}>{t.kind === "in" ? "+" : "-"}{fmt(t.amount, t.currency)}</td>
                  <td className="px-4 py-3 text-[#5C5B57] whitespace-normal break-words align-top">{t.description || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================
// ADMIN APPROVALS
// ============================================
function AdminApprovals({ branch, method, testidPrefix }) {
  const [filter, setFilter] = useState("pending");
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cash/requests", {
        params: { branch, method, status: filter, scope: "all" },
      });
      setReqs(data || []);
    } finally { setLoading(false); }
  }, [branch, method, filter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.post(`/cash/requests/${id}/approve`); toast.success("Talep onaylandı"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Onaylanamadı"); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reddetme sebebi (opsiyonel):", "");
    if (reason === null) return;
    try { await api.post(`/cash/requests/${id}/reject`, { reason }); toast.success("Talep reddedildi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reddedilemedi"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 bg-white border border-[#E6E4DD] rounded-2xl p-2 w-fit" data-testid={`${testidPrefix}-approval-filter`}>
        {[
          { k: "pending", label: "Bekleyen" },
          { k: "approved", label: "Onaylı" },
          { k: "rejected", label: "Reddedilen" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`${testidPrefix}-filter-${f.k}`}
            className={`px-4 py-1.5 rounded-xl text-xs font-medium ${filter === f.k ? "bg-[#2D2C2A] text-white" : "text-[#5C5B57] hover:bg-[#F0EFEA]"}`}
          >{f.label}</button>
        ))}
      </div>

      <div className="bg-white border border-[#E6E4DD] rounded-2xl overflow-hidden" data-testid={`${testidPrefix}-approvals-table`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C5B57] border-b border-[#E6E4DD]">
              <th className="px-4 py-3 text-left">Talep Eden</th>
              <th className="px-4 py-3 text-left">Tarih</th>
              <th className="px-4 py-3">Tür</th>
              <th className="px-4 py-3 text-right">Tutar</th>
              <th className="px-4 py-3 text-left w-[30%]">Açıklama</th>
              <th className="px-4 py-3 text-left">Durum</th>
              <th className="px-4 py-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan="7" className="px-4 py-8 text-center text-[#5C5B57]">Yükleniyor...</td></tr>)}
            {!loading && reqs.length === 0 && (<tr><td colSpan="7" className="px-4 py-8 text-center text-[#5C5B57]">Kayıt yok.</td></tr>)}
            {reqs.map((r) => (
              <tr key={r.id} className="border-b border-[#E6E4DD]/60" data-testid={`approval-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#2D2C2A]">{r.user_name || "-"}</div>
                  <div className="text-xs text-[#5C5B57]">{r.user_email}</div>
                </td>
                <td className="px-4 py-3 text-[#5C5B57] whitespace-nowrap">{formatDateTR(r.date)}</td>
                <td className="px-4 py-3"><span className={`inline-block text-xs px-2 py-0.5 rounded-full ${r.kind === "in" ? "bg-[#4A6741]/15 text-[#4A6741]" : "bg-[#B24C4C]/15 text-[#B24C4C]"}`}>{r.kind === "in" ? "Giriş" : "Çıkış"}</span></td>
                <td className={`px-4 py-3 text-right font-mono whitespace-nowrap ${r.kind === "in" ? "text-[#4A6741]" : "text-[#B24C4C]"}`}>{r.kind === "in" ? "+" : "-"}{fmt(r.amount, r.currency)}</td>
                <td className="px-4 py-3 text-[#5C5B57] whitespace-normal break-words align-top">{r.description || "-"}</td>
                <td className="px-4 py-3">
                  {r.status === "pending" && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#D4A373]/20 text-[#8A5A44]"><Clock size={11}/> Bekliyor</span>}
                  {r.status === "approved" && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#4A6741]/15 text-[#4A6741]"><CheckCircle2 size={11}/> Onaylı</span>}
                  {r.status === "rejected" && (
                    <div>
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#B24C4C]/15 text-[#B24C4C]"><XCircle size={11}/> Reddedildi</span>
                      {r.reject_reason && <div className="text-[10px] text-[#5C5B57] mt-1 italic">{r.reject_reason}</div>}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {r.status === "pending" && (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => approve(r.id)} data-testid={`approve-${r.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#4A6741] text-white text-xs hover:bg-[#3d5636]"><CheckCircle2 size={13}/> Onayla</button>
                      <button onClick={() => reject(r.id)} data-testid={`reject-${r.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#B24C4C] text-white text-xs hover:bg-[#943a3a]"><XCircle size={13}/> Reddet</button>
                    </div>
                  )}
                  {r.status !== "pending" && r.decided_by_name && (
                    <div className="text-[10px] text-[#5C5B57]">by {r.decided_by_name}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
